import {
  Button,
  Link,
  makeStyles,
  TableCell,
  TableRow,
  Theme,
  useMediaQuery,
  createStyles,
  Checkbox,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import React, { useState } from 'react';
import { PaymentsDueRowData } from '../custom-types/PaymentsDueRowData';
import { CurrencyType, PayerTransaction, PaymentRequestStatus } from '../gql-types.generated';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ErrorIcon from '@material-ui/icons/Error';
import CloseIcon from '@material-ui/icons/Close';
import { paymentStatusDisplay } from '../util/PaymentStatus';
import { DateTime } from 'luxon';
import CurrencyFormat from 'react-currency-format';

const useStylesMobile = makeStyles((theme: Theme) =>
  createStyles({
    firstTableCell: {
      padding: '5px 10px 5px 16px',
    },
    cellData: {
      display: 'flex',
      padding: '2px',
    },
    cellDetails: {
      width: '60%',
      fontSize: '16px',
    },
    cellDate: {
      fontSize: '14px',
      margin: '2px 1px 0px auto',
      width: '40%',
      textAlign: 'right',
    },
    cellReferenceNumber: {
      marginTop: '2px',
      color: '#276EF1',
      wordBreak: 'break-word',
    },
    cellAmount: {
      color: '#000000DE',
    },
    failedCell: {
      color: '#FF3B30',
    },
    errorIcon: {
      fontSize: '20px',
      verticalAlign: 'text-bottom',
    },
    consolidatedAmount: {
      width: '85%',
      color: theme.palette.grey[600],
      fontSize: '18px',
      textAlign: 'right',
    },
    closeIconDiv: {
      width: '15%',
      textAlign: 'right',
      color: theme.palette.grey[600],
    },
    partialCellData: {
      display: 'flex',
      padding: '2px',
      alignItems: 'center',
    },
    partialAmount: {
      width: '90%',
    },
    partialCloseIconDiv: {
      width: '15%',
      textAlign: 'center',
      color: theme.palette.grey[600],
    },
    textField: {
      backgroundColor: '#FFFFFF',
    },
    totalBeforeDiscount: {
      textDecoration: 'line-through',
      paddingRight: theme.spacing(0.5),
      display: 'inline-block',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    total: {
      display: 'inline-block',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    cellDisplay: {
      display: 'inline-block',
    },
    cellDiscount: {
      display: 'inline-block',
      fontStyle: 'italic',
    },
    dueErrorIcon: {
      verticalAlign: 'text-bottom',
      fontSize: '17px',
      marginRight: theme.spacing(0.5),
    },
    tableRow: {
      '&$selected, &$selected:hover': {
        backgroundColor: '#EEF4FE',
      },
    },
    selected: {},
  }),
);

const useStylesDesktop = makeStyles((theme: Theme) =>
  createStyles({
    tableCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      padding: '16px 4px',
    },
    firstTableCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      padding: '16px 4px',
    },
    leftAlignedTableCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      padding: '16px 4px',
      paddingLeft: 30,
    },
    errorIcon: {
      fontSize: '20px',
      verticalAlign: 'text-bottom',
    },
    failedCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      padding: '16px 4px',
      color: '#FF3B30',
      paddingLeft: 30,
    },
    referenceCell: {
      fontSize: 16,
      color: '#276EF1',
      wordWrap: 'break-word',
      wordBreak: 'break-word',
      padding: '16px 4px',
      paddingLeft: 30,
    },
    consolidatedAmount: {
      width: '90%',
      color: theme.palette.grey[800],
      textAlign: 'right',
    },
    closeIconDiv: {
      width: '10%',
      textAlign: 'right',
      color: theme.palette.grey[800],
    },
    cellData: {
      display: 'flex',
      alignItems: 'center',
    },
    partialAmount: {
      width: '85%',
    },
    tableRow: {
      '&$selected, &$selected:hover': {
        backgroundColor: '#EEF4FE',
      },
    },
    selected: {},
    lastCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      padding: '16px 16px 16px 4px',
    },
    totalBeforeDiscount: {
      textDecoration: 'line-through',
      display: 'inline-block',
    },
    textField: {
      backgroundColor: '#FFFFFF',
    },
    total: {
      display: 'inline-block',
      paddingLeft: theme.spacing(0.5),
    },
  }),
);

interface PaymentsDueRowProps {
  data: PaymentsDueRowData;
  rowIndex: number;
  isMobileSize: boolean;
  consolidatedAllowed: boolean;
  partialAllowed: boolean;
  creditAllowed: boolean;
  settingsMenuVisible: boolean;
  onClickPay: (urlToken: string | null | undefined) => void;
  onRowClicked: (index: number) => void;
  showPaymentDetails: (paymentDetails: PayerTransaction | null | undefined, detailsIndex?: number, isSelected?: boolean) => void;
  handleRowAmountChange: (index: number, amount: number) => void;
  handleAmountError: (hasError: boolean) => void;
  amountError: number;
  discountEndVisible: boolean;
}

const PaymentsDueRow: React.FC<PaymentsDueRowProps> = props => {
  const {
    data,
    rowIndex,
    consolidatedAllowed,
    partialAllowed,
    isMobileSize,
    settingsMenuVisible,
    onClickPay,
    onRowClicked,
    showPaymentDetails,
    handleRowAmountChange,
    handleAmountError,
    amountError,
    discountEndVisible,
    creditAllowed,
  } = props;

  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const dueDate = DateTime.fromISO(data.paymentDue.paymentRequest?.dueDate);
  const overDue = dueDate.isValid && dueDate.startOf('day') < DateTime.now().startOf('day');
  const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const discountEndDate = DateTime.fromISO(data.paymentDue.paymentRequest?.discountEndDate);
  const doesDiscountApply = discountEndDate.isValid && discountEndDate > DateTime.utc();
  const discountAmount =
    doesDiscountApply && data.paymentDue?.paymentRequest?.discountAmount ? data.paymentDue.paymentRequest.discountAmount : 0;

  const [amount, setAmount] = useState<string>((data.amount / 100).toFixed(2).toString());
  const [isValidAmount, setIsValidAmount] = useState<boolean>(data.amount <= (data.paymentDue.paymentRequest?.totalDue || 0));

  const isAmountValid = (amount: number): boolean => {
    if (amount > (data.paymentDue.paymentRequest?.totalDue || 0) - discountAmount || amount < 100) return false;
    return true;
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setAmount(value);
    const amount = parseFloat(value && value.length > 0 ? value : '0') * 100;
    const isValidPrevious = isValidAmount;
    const isValid = isAmountValid(amount);
    handleRowAmountChange(rowIndex, amount);
    setIsValidAmount(isValid);
    if (isValidPrevious !== isValid) {
      if (isValid) {
        handleAmountError(false);
      } else {
        handleAmountError(true);
      }
    }
  };

  const handleMakePayment = () => {
    if (consolidatedAllowed || partialAllowed) {
      onRowClicked(rowIndex);
      if (!isValidAmount) {
        handleAmountError(false);
      }
      setAmount((data.amount / 100).toFixed(2).toString());
      setIsValidAmount(true);
    } else {
      onClickPay(data.paymentDue.paymentUrlToken);
    }
  };

  const getPaymentMethodAmount = () => {
    let amount = (data.paymentDue.paymentRequest?.totalPaid || 0) - data.creditAmount;
    amount = amount < 0 ? 0 : amount;

    return amount;
  };

  if (isMobileSize || (settingsMenuVisible && matches)) {
    const classesMobile = useStylesMobile();
    return (
      <>
        <TableRow
          className={classesMobile.tableRow}
          key={data.paymentDue.paymentRequest?.id}
          hover
          selected={data.isSelected}
          classes={{ selected: classesMobile.selected }}
        >
          <TableCell className={classesMobile.firstTableCell}>
            <div className={classesMobile.cellData}>
              <div className={classesMobile.cellDetails}>
                <div className={classesMobile.cellReferenceNumber}>
                  <Link
                    href="#"
                    onClick={() => {
                      const detailsIndex = consolidatedAllowed || partialAllowed ? rowIndex : undefined;
                      const detailsSelected = consolidatedAllowed || partialAllowed ? data.isSelected : undefined;
                      showPaymentDetails(data.paymentDue, detailsIndex, detailsSelected);
                    }}
                  >
                    {data.paymentDue.paymentRequest?.referenceNumber}
                  </Link>
                </div>
                <div
                  className={data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed ? classesMobile.failedCell : ''}
                >
                  {
                    paymentStatusDisplay.find(item => {
                      return item.name === data.paymentDue.paymentRequest?.status;
                    })?.display
                  }
                </div>
                <div
                  className={
                    data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed
                      ? classesMobile.failedCell
                      : classesMobile.cellAmount
                  }
                >
                  {data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed && (
                    <ErrorOutlineIcon className={classesMobile.errorIcon} />
                  )}
                  {discountAmount > 0 && typeof data.paymentDue.paymentRequest?.totalDue === 'number' && (
                    <Typography className={classesMobile.totalBeforeDiscount} color="error">
                      {typeof data.paymentDue.paymentRequest?.totalDue === 'number'
                        ? new Intl.NumberFormat('en', {
                            style: 'currency',
                            currency: data.paymentDue.currency || 'USD',
                            currencyDisplay: 'symbol',
                          }).format(data.paymentDue.paymentRequest?.totalDue / 100)
                        : null}
                    </Typography>
                  )}
                  {typeof data.paymentDue.paymentRequest?.totalDue === 'number' ? (
                    <Typography className={classesMobile.total}>
                      {new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: data.paymentDue.currency || 'USD',
                        currencyDisplay: 'symbol',
                      }).format((data.paymentDue.paymentRequest?.totalDue - discountAmount) / 100)}
                    </Typography>
                  ) : null}
                </div>
              </div>
              <div className={classesMobile.cellDate}>
                <div>
                  <div className={classesMobile.cellDisplay}>
                    Billed {new Date(data.paymentDue.paymentRequest?.createdAt).toLocaleDateString()}
                  </div>
                </div>
                {data.paymentDue.paymentRequest?.dueDate && (
                  <div>
                    <div className={classesMobile.cellDisplay}>
                      {overDue && <ErrorIcon color="error" className={classesMobile.dueErrorIcon} />}
                      Due {new Date(data.paymentDue.paymentRequest?.dueDate).toLocaleDateString()}
                    </div>
                  </div>
                )}
                {data.paymentDue.paymentRequest?.discountEndDate && (
                  <div>
                    <div className={classesMobile.cellDiscount}>
                      Discount Ends{' '}
                      {new Date(data.paymentDue.paymentRequest?.discountEndDate).toLocaleDateString(undefined, {
                        month: 'numeric',
                        day: 'numeric',
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* partial payment allowed and selected */}
            {partialAllowed && data.isSelected && (
              <div className={classesMobile.partialCellData}>
                <div className={classesMobile.partialAmount}>
                  <CurrencyFormat
                    displayType="input"
                    customInput={TextField}
                    decimalScale={2}
                    allowNegative={false}
                    autoFocus
                    variant="outlined"
                    margin="none"
                    size="small"
                    fullWidth={true}
                    error={!isValidAmount}
                    value={amount}
                    classes={{ root: classesMobile.textField }}
                    onChange={handleAmountChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography>{`${data.paymentDue.currency === CurrencyType.Cad ? 'CA' : ''}$`}</Typography>
                        </InputAdornment>
                      ),
                    }}
                    data-cy="amount-edit"
                  />
                </div>
                <div className={matchesSm ? classesMobile.partialCloseIconDiv : classesMobile.closeIconDiv}>
                  <IconButton onClick={handleMakePayment} size="medium" disableFocusRipple disableRipple>
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
            )}

            {/* only consolidated payment allowed and selected */}
            {consolidatedAllowed && !partialAllowed && data.isSelected && (
              <div className={classesMobile.cellData}>
                <div className={classesMobile.consolidatedAmount}>
                  {typeof data.paymentDue.paymentRequest?.totalDue === 'number'
                    ? new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: data.paymentDue.currency || 'USD',
                        currencyDisplay: 'symbol',
                      }).format((data.paymentDue.paymentRequest?.totalDue - discountAmount) / 100)
                    : null}
                </div>
                <div className={classesMobile.closeIconDiv}>
                  <IconButton onClick={handleMakePayment} size="small" disableFocusRipple disableRipple>
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
            )}

            {/* consolidated and partial not allowed or not selected */}
            {!((consolidatedAllowed || partialAllowed) && data.isSelected) && (
              <Button variant="outlined" color="primary" fullWidth onClick={handleMakePayment} disabled={amountError !== 0}>
                {data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed ? 'RETRY PAYMENT' : 'MAKE PAYMENT'}
              </Button>
            )}
          </TableCell>
        </TableRow>
      </>
    );
  }

  const classesDesktop = useStylesDesktop();

  return (
    <>
      <TableRow
        className={classesDesktop.tableRow}
        key={data.paymentDue.paymentRequest?.id}
        hover
        selected={data.isSelected}
        classes={{ selected: classesDesktop.selected }}
      >
        <TableCell align="right" className={classesDesktop.firstTableCell}>
          <Checkbox
            color="primary"
            checked={data.isSelected}
            disabled={!(consolidatedAllowed || partialAllowed)}
            onClick={handleMakePayment}
          />
        </TableCell>
        <TableCell align="left" className={classesDesktop.referenceCell}>
          <Link
            href="#"
            onClick={() => {
              const detailsIndex = consolidatedAllowed || partialAllowed ? rowIndex : undefined;
              const detailsSelected = consolidatedAllowed || partialAllowed ? data.isSelected : undefined;
              showPaymentDetails(data.paymentDue, detailsIndex, detailsSelected);
            }}
          >
            {data.paymentDue.paymentRequest?.referenceNumber}
          </Link>
        </TableCell>
        {discountEndVisible && (
          <TableCell align="left" className={classesDesktop.leftAlignedTableCell}>
            <Grid item container alignItems="center">
              {data.paymentDue.paymentRequest?.discountEndDate
                ? new Date(data.paymentDue.paymentRequest?.discountEndDate).toLocaleDateString()
                : ''}
            </Grid>
          </TableCell>
        )}
        <TableCell align="left" className={classesDesktop.leftAlignedTableCell}>
          <Grid item container alignItems="center">
            {data.paymentDue.paymentRequest?.dueDate ? new Date(data.paymentDue.paymentRequest?.dueDate).toLocaleDateString() : ''}
            {overDue && <ErrorIcon color="error" fontSize="small" />}
          </Grid>
        </TableCell>
        <TableCell align="left" className={classesDesktop.leftAlignedTableCell}>
          {new Date(data.paymentDue.paymentRequest?.createdAt).toLocaleDateString()}
        </TableCell>
        <TableCell
          align="left"
          className={
            data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed
              ? classesDesktop.failedCell
              : classesDesktop.leftAlignedTableCell
          }
        >
          {
            paymentStatusDisplay.find(item => {
              return item.name === data.paymentDue.paymentRequest?.status;
            })?.display
          }
        </TableCell>
        <TableCell
          align="right"
          className={
            data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed
              ? classesDesktop.failedCell
              : classesDesktop.tableCell
          }
        >
          {data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed && (
            <ErrorOutlineIcon className={classesDesktop.errorIcon} />
          )}
          {discountAmount > 0 && typeof data.paymentDue.paymentRequest?.amount === 'number' && (
            <Typography className={classesDesktop.totalBeforeDiscount} color="error">
              {typeof data.paymentDue.paymentRequest?.amount === 'number'
                ? new Intl.NumberFormat('en', {
                    style: 'currency',
                    currency: data.paymentDue.currency || 'USD',
                    currencyDisplay: 'symbol',
                  }).format(data.paymentDue.paymentRequest?.amount / 100)
                : null}
            </Typography>
          )}
          {typeof data.paymentDue.paymentRequest?.amount === 'number' ? (
            <Typography className={classesDesktop.total}>
              {new Intl.NumberFormat('en', {
                style: 'currency',
                currency: data.paymentDue.currency || 'USD',
                currencyDisplay: 'symbol',
              }).format((data.paymentDue.paymentRequest?.amount - discountAmount) / 100)}
            </Typography>
          ) : null}
        </TableCell>
        {creditAllowed && partialAllowed && (
          <TableCell align="right" className={classesDesktop.tableCell}>
            {new Intl.NumberFormat('en', {
              style: 'currency',
              currency: data.paymentDue.currency || 'USD',
              currencyDisplay: 'symbol',
            }).format(data.creditAmount / 100)}
          </TableCell>
        )}
        {partialAllowed && (
          <TableCell align="right" className={classesDesktop.tableCell}>
            {new Intl.NumberFormat('en', {
              style: 'currency',
              currency: data.paymentDue.currency || 'USD',
              currencyDisplay: 'symbol',
            }).format(getPaymentMethodAmount() / 100)}
          </TableCell>
        )}
        {partialAllowed && (
          <TableCell align="right" className={classesDesktop.tableCell}>
            {typeof data.paymentDue.paymentRequest?.totalDue === 'number'
              ? new Intl.NumberFormat('en', {
                  style: 'currency',
                  currency: data.paymentDue.currency || 'USD',
                  currencyDisplay: 'symbol',
                }).format((data.paymentDue.paymentRequest?.totalDue - discountAmount) / 100)
              : null}
          </TableCell>
        )}
        <TableCell align="right" className={classesDesktop.lastCell}>
          {/* partial payment allowed and selected */}
          {partialAllowed && data.isSelected && (
            <div className={classesDesktop.cellData} style={{ justifyContent: 'flex-end' }}>
              <div className={classesDesktop.partialAmount}>
                <CurrencyFormat
                  displayType="input"
                  customInput={TextField}
                  decimalScale={2}
                  allowNegative={false}
                  autoFocus
                  variant="outlined"
                  margin="none"
                  size="small"
                  error={!isValidAmount}
                  value={amount}
                  classes={{ root: classesDesktop.textField }}
                  onChange={handleAmountChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography>{`${data.paymentDue.currency === CurrencyType.Cad ? 'CA' : ''}$`}</Typography>
                      </InputAdornment>
                    ),
                  }}
                  data-cy="amount-edit"
                />
              </div>
              <div className={classesDesktop.closeIconDiv}>
                <IconButton onClick={handleMakePayment} size="medium" disableFocusRipple disableRipple>
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          )}

          {/* only consolidated payment allowed and selected */}
          {consolidatedAllowed && !partialAllowed && data.isSelected && (
            <div className={classesDesktop.cellData}>
              <div className={classesDesktop.consolidatedAmount}>
                {typeof data.paymentDue.paymentRequest?.totalDue === 'number'
                  ? new Intl.NumberFormat('en', {
                      style: 'currency',
                      currency: data.paymentDue.currency || 'USD',
                      currencyDisplay: 'symbol',
                    }).format((data.paymentDue.paymentRequest?.totalDue - discountAmount) / 100)
                  : null}
              </div>
              <div className={classesDesktop.closeIconDiv}>
                <IconButton onClick={handleMakePayment} size="small" disableFocusRipple disableRipple>
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          )}

          {/* consolidated and partial not allowed or not selected */}
          {!((consolidatedAllowed || partialAllowed) && data.isSelected) && (
            <Button variant="outlined" color="primary" onClick={handleMakePayment} disabled={amountError !== 0}>
              {data.paymentDue.paymentRequest?.status === PaymentRequestStatus.Failed ? 'RETRY PAYMENT' : 'MAKE PAYMENT'}
            </Button>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

export default PaymentsDueRow;
